import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { cn } from '@strivelabs/ui-core/lib/utils';
import type { WorkflowExecution } from '@/hooks/useWorkflowExecutions';
import { titleOf } from '../../hooks/useChatList';
import { ChatRowMenu } from './ChatRowMenu';

export interface ChatRowProps {
  execution: WorkflowExecution;
  isActive: boolean;
  onRename: () => void;
  onDelete: () => void;
}

/**
 * One conversation. Presentational — the row takes callbacks rather than
 * mutations, so it can be rendered in a test without a QueryClient.
 *
 * Geometry is the nav tree's, so the two tabs read as one rail: a 26px row
 * with an 8px radius, a 28×32 icon box, and a 13px label at 78% ink (56% for
 * the mark). The row itself carries the tint rather than the link, so the
 * action strip can inherit it and let a long title run to the edge until
 * there is something to show there.
 */
export function ChatRow({ execution, isActive, onRename, onDelete }: ChatRowProps) {
  const title = titleOf(execution);

  return (
    <li
      className={cn(
        'group relative flex h-[26px] items-center rounded-v2-lg text-[13px] leading-[18px]',
        'text-v2-sidebar-foreground/[0.78] transition-colors duration-v2-quick',
        'hover:bg-v2-sidebar-accent focus-within:bg-v2-sidebar-accent',
        isActive && 'bg-v2-sidebar-active font-medium text-v2-sidebar-accent-foreground',
      )}
    >
      <Link
        to={`/ui/chat/${execution.id}`}
        aria-current={isActive ? 'page' : undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-0.5 rounded-v2-lg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'grid h-8 w-7 shrink-0 place-items-center',
            isActive ? 'text-v2-sidebar-accent-foreground' : 'text-v2-sidebar-foreground/[0.56]',
          )}
        >
          <MessageCircle size={12} />
        </span>
        <span className="truncate pr-1">{title}</span>
      </Link>
      {/* Paints the row's own tint, so the title tucks under the actions the
          moment they appear instead of the row reserving width for them. */}
      <span className="absolute inset-y-0 right-0.5 flex items-center rounded-v2-lg bg-inherit pl-3">
        <ChatRowMenu chatTitle={title} onRename={onRename} onDelete={onDelete} />
      </span>
    </li>
  );
}
